import { Component } from 'preact';
import style from './styles/CustomerStep.scss';
import RadioSelect from '../../form/RadioSelect';
import AccountOptions from '../../../enums/AccountOptions';
import GenderOptions from '../../../enums/GenderOptions';
import TextInput from '../../form/TextInput';
import Validator from '../../form/Validators';
import SlideDownReveal from '../../effects/SlideDownReveal';
import BirthdayInput from '../../form/BirthdayInput';
import SelectInput from '../../form/SelectInput';
import CheckboxInput from '../../form/CheckboxInput';
import RegistrationRepository from '../../../repositories/RegistrationRepository';
import { Form } from 'reactstrap';
import { ContinueButton } from '../../form/Buttons';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
	goToSummaryStepAction,
	updateAccordionsAction,
	updateBirthdayInputAction,
	updateConsentCheckboxInput,
	updateSelectInputAction,
	updateDeliveryServiceAction,
	updateTextInputAction,
	updateCountryAction
} from '../../../actions/RegistrationFormActions';
import { updateRomanianCityOptionsAction, updateAddressComponentsAction } from '../../../actions/RaffleFormActions';
import { closeConsentModalAction, openConsentModalAction } from '../../../actions/ConsentModalActions';
import { CLIENT } from '../../../parameters';
import DeliveryRepository from '../../../repositories/DeliveryRepository';
import LoadingSpinner from '../../spinner/LoadingSpinner';
import Countries from '../../../enums/Countries';
import Modal from '../../ui/Modal';
import ConsentLink from './ConsentLink';
import ConsentDetail from './ConsentDetail';

/**
 * @typedef {Object} SelectData
 * @property {string} value
 * @property {boolean} touched
 * @property {SelectOption[]} options
 */

/**
 * @typedef {Object} CustomerStepData
 * @property {SelectData} account
 * @property {TextInputData} email
 * @property {TextInputData} phone
 * @property {SelectData} gender
 * @property {TextInputData} firstName
 * @property {TextInputData} lastName
 * @property {TextInputData} instagramUsername
 * @property {SelectData} birthday
 * @property {TextInputData} streetName
 * @property {TextInputData} houseNumber
 * @property {TextInputData} additional
 * @property {TextInputData} postalCode
 * @property {SelectData} city
 * @property {SelectData} usState
 * @property {SelectData} caState
 * @property {SelectData} romanianCity
 * @property {SelectData} romanianCounty
 * @property {SelectData} country
 * @property {Object} consents
 */

/**
 * @property {string[]} props.checkedSteps
 * @property {Function} props.onSubmitStep
 * @property {Function} props.updateAccordionsAction
 * @property {Function} props.updateConsentCheckboxInput
 * @property {Function} props.updateTextInputAction
 * @property {Function} props.updateSelectInputAction
 * @property {Function} props.updateBirthdayInputAction
 * @property {Function} props.updateCountryAction
 * @property {ConsentData[]} props.consents
 * @property {SelectOption[]} props.countries
 *
 * Note: For autocompletes, see https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofilling-form-controls:-the-autocomplete-attribute
 */
class CustomerStep extends Component {

	static isValid = (data) => CustomerStep.isContactAccordionValid(data)
		&& CustomerStep.isAboutYouAccordionValid(data)
		&& CustomerStep.isDeliveryAddressAccordionValid(data)
	;

	/**
	 * @param {CustomerStepData} data
	 *
	 * @return boolean
	 */
	static isContactAccordionValid(data) {
		return data.email.valid
			&& data.phone.valid
		;
	}

	/**
	 * @param {CustomerStepData} data
	 *
	 * @return boolean
	 */
	static isAboutYouAccordionValid(data) {
		return data.firstName.valid
			&& data.lastName.valid
			&& data.instagramUsername.valid
			&& ( // If any of the birthday fields is filled in, all other become required
				(data.birthday.day || data.birthday.month || data.birthday.year)
					? !!(data.birthday.day && data.birthday.month && data.birthday.year)
					: true
			)
		;
	}

	/**
	 * @param {CustomerStepData} data
	 *
	 * @return boolean
	 */
	static isDeliveryAddressAccordionValid(data) {
		const consentValues = Object.keys(data.consents).map(key => data.consents[key].value);
		const usStateValid = (data.country.value === Countries.USA.value ? data.usState.value !== '' : true);
		const caStateValid = (data.country.value === Countries.CANADA.value ? data.caState.value !== '' : true);
		const romaniaCountyValid = (data.country.value === Countries.ROMANIA.value ? data.romanianCounty.value !== '' : true);
		const cityValid = (data.country.value === Countries.ROMANIA.value ? data.romanianCity.value !== '' : data.city.valid);
		return data.streetName.valid
			&& data.houseNumber.valid
			&& data.additional.valid
			&& data.postalCode.valid
			&& cityValid
			&& data.country.value !== ''
			&& usStateValid
			&& caStateValid
			&& romaniaCountyValid
			&& consentValues.every(checked => checked) // For now, all consents are required
		;
	}

	static inputs = {
		email: {
			name: 'email',
			placeholder: 'E-mail',
			autocomplete: 'email',
			required: true,
			validators: [
				new Validator('isEmail', 'email')
			]
		},
		phone: {
			name: 'phone',
			placeholder: 'Mobile phone number',
			autocomplete: 'tel',
			// Open numeric keyboard on mobile phones
			type: 'tel',
			required: true,
			validators: [
				new Validator('matchRegexp', 'phone', { regexp: /^\(?\+?[0-9]{1,3}\)? ?-?[0-9]{1,3} ?-?[0-9]{3,5} ?-?[0-9]{3,4}( ?-?[0-9]{3})?$/ }, 'Please enter valid phone number.')
			]
		},
		firstName: {
			name: 'firstName',
			placeholder: 'First Name',
			autocomplete: 'given-name',
			required: true,
			validators: [
				new Validator('minMaxStringLength', 'length', { min: 1, max: 32 })
			]
		},
		lastName: {
			name: 'lastName',
			placeholder: 'Last Name',
			autocomplete: 'family-name',
			required: true,
			validators: [
				new Validator('minMaxStringLength', 'length', { min: 1, max: 32 })
			]
		},
		instagramUsername: {
			name: 'instagramUsername',
			placeholder: 'Instagram username',
			required: true,
			validators: [
				new Validator('minMaxStringLength', 'length', { min: 1, max: 30 })
			]
		},
		streetName: {
			name: 'streetName',
			placeholder: 'Street name',
			autocomplete: 'shipping street-address',
			required: true,
			validators: [
				new Validator('minMaxStringLength', 'length', { min: 2, max: 128 })
			]
		},
		houseNumber: {
			name: 'houseNumber',
			placeholder: 'House Number',
			autocomplete: 'off',
			required: true,
			validators: [
				new Validator('minMaxStringLength', 'length', { min: 1, max: 128 })
			]
		},
		additional: {
			name: 'additional',
			autocomplete: 'off',
			placeholder: 'Additional (company reception, etc...)',
			required: false,
			validators: []
		},
		postalCode: {
			name: 'postalCode',
			placeholder: 'Postal Code',
			autocomplete: 'shipping postal-code'
		},
		city: {
			name: 'city',
			placeholder: 'City',
			autocomplete: 'shipping address-level2',
			required: true,
			validators: [
				new Validator('minMaxStringLength', 'length', { min: 2, max: 64 })
			]
		},
		country: {
			name: 'country',
			required: true,
			invalidMessage: 'Please select a country',
			placeholder: 'Country'
		},
		usState: {
			name: 'usState',
			required: true,
			invalidMessage: 'Please select your state',
			placeholder: 'State'
		},
		caState: {
			name: 'caState',
			required: true,
			invalidMessage: 'Please select your state',
			placeholder: 'State'
		},
		romanianCounty: {
			name: 'romanianCounty',
			required: true,
			invalidMessage: 'Please select a county',
			placeholder: 'County'
		},
		romanianCity: {
			name: 'romanianCity',
			required: true,
			invalidMessage: 'Please select your city',
			placeholder: 'City'
		},
		consents: {
			namePrefix: 'consent::',
			invalidMessage: 'Your consent is required'
		},
		account: {
			name: 'account'
		},
		gender: {
			name: 'gender'
		},
		birthday: {
			name: 'birthday'
		}
	};

	submitStep = () => {
		const { goToSummaryStepAction, updateAccordionsAction } = this.props;
		if (CustomerStep.isValid(this.props.data)) {
			goToSummaryStepAction();
		}
		else {
			const { contactAccordion, aboutYouAccordion, deliveryAddressAccordion } = this.props;
			updateAccordionsAction({
				contactAccordion: { ...contactAccordion, wasValidated: true },
				aboutYouAccordion: { ...aboutYouAccordion, wasValidated: true },
				deliveryAddressAccordion: { ...deliveryAddressAccordion, wasValidated: true }
			});
		}
	};

	/**
	 * Open contact accordion and close others
	 *
	 * @param {Event} event
	 * @this CustomerStep
	 */
	openContactAccordion = (event) => {
		const { contactAccordion, aboutYouAccordion, deliveryAddressAccordion } = this.props;
		this.props.updateAccordionsAction({
			contactAccordion: { ...contactAccordion, isOpen: true, isVisible: true },
			aboutYouAccordion: { ...aboutYouAccordion, isOpen: false },
			deliveryAddressAccordion: { ...deliveryAddressAccordion, isOpen: false }
		});
	};

	/**
	 * Open about you accordion and close others
	 *
	 * @param {Event} event
	 * @this CustomerStep
	 */
	openAboutYouAccordion = (event) => {
		const {
			raffle,
			registrationId,
			contactAccordion,
			checkContactDuplicityAction,
			data
		} = this.props;

		if (CustomerStep.isContactAccordionValid(data)) {
			checkContactDuplicityAction(raffle.id, data.email.value, data.phone.value, registrationId);
		}
		else {
			this.props.updateAccordionsAction({ contactAccordion: { ...contactAccordion, wasValidated: true } });
		}
	};

	/**
	 * Open delivery address accordion and close others
	 *
	 * @param {Event} event
	 * @this CustomerStep
	 */
	openDeliveryAddressAccordion = (event) => {
		const { contactAccordion, aboutYouAccordion, deliveryAddressAccordion, data } = this.props;

		if (CustomerStep.isContactAccordionValid(data) && CustomerStep.isAboutYouAccordionValid(data)) {
			this.props.updateAccordionsAction({
				contactAccordion: { ...contactAccordion, isOpen: false, isVisible: true },
				aboutYouAccordion: { ...aboutYouAccordion, isOpen: false, isVisible: true },
				deliveryAddressAccordion: { ...deliveryAddressAccordion, isOpen: true, isVisible: true }
			});
		}
		else {
			this.props.updateAccordionsAction({
				contactAccordion: { ...contactAccordion, wasValidated: true },
				aboutYouAccordion: { ...aboutYouAccordion, wasValidated: true }
			});
		}
	};

	/**
	 * @param {ConsentData} consent
	 */
	createConsentLink(consent) {
		const parsedTitle = /(.*?)<link>(.*?)<\/link>(.*?)$/.exec(consent.text);
		if (!parsedTitle) {
			return <span>{consent.text}</span>;
		}
		const [, textBeforeLink, linkText, textAfterLink] = parsedTitle;
		return <ConsentLink {...{ consent, openConsentModal: this.openConsentModal, textBeforeLink, linkText, textAfterLink }} />;
	}

	/**
	 * @param {string} name
	 * @param {Object} data
	 * @param {CountryOption} option
	 */
	onCountryChange = (name, data, option) => {
		const country = data.value;
		this.props.updateCountryAction(country);
		this.handleCountryChange(country);
	};

	/**
	 * Load additional data if necessary
	 *
	 * @param {string} country
	 */
	handleCountryChange = (country) => {
		if (country === Countries.ROMANIA.value) {
			if (this.props.romanianCountiesAndCities === null && this.props.fetchingRomanianCountiesAndCities !== true) {
				this.props.fetchRomanianCountiesAndCities();
			}
		}
	};

	onRomanianCountyChange = (name, data) => {
		this.props.updateSelectInputAction(name, data);
		this.props.updateRomanianCityOptionsAction();
	};

	onConsentModalClose = () => {
		this.props.closeConsentModalAction();
	};

	openConsentModal = (title, detailUrl) => {
		this.props.openConsentModalAction(title, detailUrl);
	};

	onAddressComponentsChange = (updatedAddressFields) => {
		this.props.updateAddressComponentsAction(updatedAddressFields);
		const country = updatedAddressFields.country;
		this.handleCountryChange(country);
	};

	onStreetNameChange = (streetName) => {
		this.props.updateTextInputAction('streetName', {
			value: streetName,
			valid: streetName.length >= 2,
			errors: [],
			touched: true
		});
	};

	componentDidMount() {
		if (CLIENT) {
			window.scrollTo(0, 0);
		}
	}

	/**
	 * @param {ConsentData} consent
	 * @param {boolean} data
	 * @param {boolean} parentWasValidated
	 */
	renderConsent = (consent, data, parentWasValidated) => (
		<CheckboxInput {...{
			...data,
			parentWasValidated,
			invalidMessage: CustomerStep.inputs.consents.invalidMessage,
			className: style.consent,
			required: true,
			id: 'consent::'+consent.consentVersionId,
			name: CustomerStep.inputs.consents.namePrefix + consent.consentVersionId,
			label: this.createConsentLink(consent),
			onChangeAction: this.props.updateConsentCheckboxInput
		}}
		/>
	);

	/**
	 * @param {string} name
	 * @param {CustomerStepData} data
	 * @param {boolean} parentWasValidated
	 * @param {Object} override
	 *
	 * @this CustomerStep
	 */
	renderTextInput = (name, data, parentWasValidated, override = {}) => (
		<TextInput {...{ ...data[name], ...CustomerStep.inputs[name], ...override, parentWasValidated, onChangeAction: this.props.updateTextInputAction }} />
	);

	render() {
		const inputs = CustomerStep.inputs;
		const {
			contactAccordion, aboutYouAccordion, deliveryAddressAccordion, checkingContactDuplicity, birthdayIsRequired,
			birthdayIsComplete, fetchingRomanianCountiesAndCities, consentModal
		} = this.props;
		// Keep phpstorm type hinting intact (ctrl+q)
		const data = this.props.data;
		const consents = this.props.consents;
		const hideAccountOptions = true;
		const consentModalFooter = <button type="button" className="btn btn-secondary" data-dismiss="modal" onClick={this.onConsentModalClose}>Close</button>;

		const postalCodeConfig = {
			required: data.country.value !== Countries.ROMANIA.value && data.country.value !== Countries.HONGKONG.value,
			validators: [
				new Validator('callback', 'notBlankUnlessRomania', (value) => {
					if (data.country.value === Countries.ROMANIA.value || data.country.value === Countries.HONGKONG.value) {
						return true;
					}
					return !!value;
				}, 'Value cannot be empty.')
			]
		};

		return (
			<Form novalidate>
				<div className={`row form-style ${style['customer-step']}`}>
					{ /** Temporally hide Account options*/ hideAccountOptions ? null : (
						<div className={`col-12 ${style['account-options-wrapper']}`}>
							<RadioSelect {...{ ...data.account, ...inputs.account, options: AccountOptions.toOptions(), onChangeAction: this.props.updateSelectInputAction }} />
						</div>)
					}
					<Accordion title="Contact" {...contactAccordion} onEditClick={this.openContactAccordion} key={'contact-accordion'}>
						{this.renderTextInput('email', data, contactAccordion.wasValidated)}
						{this.renderTextInput('phone', data, contactAccordion.wasValidated)}
						<div className={`m-3`}>
							<ContinueButton enabled={contactAccordion.isChecked} disabled={checkingContactDuplicity} enabledMessage={'Continue to your identity'} onClick={this.openAboutYouAccordion} spinner={checkingContactDuplicity} />
						</div>
					</Accordion>
					<Accordion title="About you" {...aboutYouAccordion} onEditClick={this.openAboutYouAccordion} key={'about-you-accordion'}>
						<div className={`${style['desc-wrapper']}`}>To enter the raffle you have to follow us on <a href="https://www.instagram.com/footshop/" rel="noopener" target="_blank">Instagram</a></div>
						<div className={`col-12 inline-select-options ${style['gender-options-wrapper']}`}>
							<RadioSelect {...{ ...data.gender, ...inputs.gender, options: GenderOptions.toOptions(), onChangeAction: this.props.updateSelectInputAction }} />
						</div>
						{this.renderTextInput('firstName', data, aboutYouAccordion.wasValidated)}
						{this.renderTextInput('lastName', data, aboutYouAccordion.wasValidated)}
						{this.renderTextInput('instagramUsername', data, aboutYouAccordion.wasValidated)}
						<div className={`mt-3 mx-3 mb-1`}>
							<label className={`${style.label} ${style.required}`}>Your birthday date</label>
							<BirthdayInput {...{ ...data.birthday, ...inputs.birthday, onChangeAction: this.props.updateBirthdayInputAction, parentWasValidated: aboutYouAccordion.wasValidated }} />
							<div className={style['birthday-msg-wrapper']}>
								<SlideDownReveal visible={birthdayIsRequired} appear>
									{birthdayIsComplete
										? <div className={'input-msg valid'}>Looks good!</div>
										: (aboutYouAccordion.wasValidated ? <div className={'input-msg'}>Please fill in day, month and year.</div> : null)
									}
								</SlideDownReveal>
							</div>
						</div>
						<div className={`mx-3 my-2`}>
							<ContinueButton enabled={aboutYouAccordion.isChecked} enabledMessage={'Continue to delivery address'} onClick={this.openDeliveryAddressAccordion} />
						</div>
					</Accordion>
					<Accordion title="Delivery address" {...deliveryAddressAccordion} onEditClick={this.openDeliveryAddressAccordion} key={'delivery-address-accordion'}>
						<p className={style['delivery-information']}>We deliver to all countries outside the European Union on a duty-free basis. This means, that the recipient will be liable for any local sales tax or import duties charged by the delivery courier. Rates and taxes are set by the target country. We cant be held responsible if the local customs authorities delay the process or want to seize any items or collect any import duties. The beneficiary is responsible for paying the tax. The duty is payable by the payee to the courier by credit card or in cash upon delivery of the shipment.</p>
						<div className={`m-3`}>
							<SelectInput {...{ ...inputs.country, ...data.country, onChangeAction: this.onCountryChange, className: style.country, parentWasValidated: deliveryAddressAccordion.wasValidated }} />
						</div>
						<SlideDownReveal visible={data.country.value === Countries.USA.value} appear>
							<div className={`m-3`}>
								<SelectInput {...{ ...inputs.usState, ...data.usState, onChangeAction: this.props.updateSelectInputAction, className: style.country, parentWasValidated: deliveryAddressAccordion.wasValidated }} />
							</div>
						</SlideDownReveal>
						<SlideDownReveal visible={data.country.value === Countries.CANADA.value} appear>
							<div className={`m-3`}>
								<SelectInput {...{ ...inputs.caState, ...data.caState, onChangeAction: this.props.updateSelectInputAction, className: style.country, parentWasValidated: deliveryAddressAccordion.wasValidated }} />
							</div>
						</SlideDownReveal>
						<SlideDownReveal visible={fetchingRomanianCountiesAndCities} delay={500}>
							<div className={`m-3`}>
								<LoadingSpinner active className={'text-center'} />
							</div>
						</SlideDownReveal>
						<SlideDownReveal visible={data.country.value === Countries.ROMANIA.value && this.props.romanianCountiesAndCities} delay={100}>
							<div className={`m-3`}>
								<SelectInput {...{ ...inputs.romanianCounty, ...data.romanianCounty, onChangeAction: this.onRomanianCountyChange, className: style.country, parentWasValidated: deliveryAddressAccordion.wasValidated }} />
							</div>
							<div className={`m-3`}>
								<SelectInput {...{ ...inputs.romanianCity, ...data.romanianCity, disabled: !data.romanianCounty.value, onChangeAction: this.props.updateSelectInputAction, className: style.country, parentWasValidated: deliveryAddressAccordion.wasValidated }} />
							</div>
						</SlideDownReveal>

						{this.renderTextInput('streetName', data, deliveryAddressAccordion.wasValidated)}
						{this.renderTextInput('houseNumber', data, deliveryAddressAccordion.wasValidated)}
						{this.renderTextInput('additional', data, deliveryAddressAccordion.wasValidated)}
						{this.renderTextInput('postalCode', data, deliveryAddressAccordion.wasValidated, postalCodeConfig )}
						{ data.country.value === Countries.ROMANIA.value
							? null
							: this.renderTextInput('city', data, deliveryAddressAccordion.wasValidated)
						}
						<div className={`m-3`}>
							<div className={style.consents}>
								{consents.map((consent) => this.renderConsent(consent, data.consents[consent.consentVersionId], deliveryAddressAccordion.wasValidated))}
							</div>
						</div>
						<div className={`m-3`}>
							<ContinueButton
								enabled={deliveryAddressAccordion.isChecked}
								enabledMessage={'Proceed to summary'}
								onClick={this.submitStep}
							/>
						</div>
					</Accordion>
				</div>
				<Modal {...{ onHide: this.onConsentModalClose, title: consentModal.title, show: consentModal.show, footer: consentModalFooter, modalClasses: 'modal-dialog-centered modal-lg' }}>
					<ConsentDetail detailUrl={consentModal.detailUrl} />
				</Modal>
			</Form>
		);
	}
}

const mapStateToProps = (state) => {
	const {
		raffle, fields, accordions, consents, countries, checkingContactDuplicity, romanianCountiesAndCities,
		fetchingRomanianCountiesAndCities, consentModal, registration
	} = state.raffleForm;

	return {
		raffle,
		consents,
		countries,
		registrationId: registration ? registration.id : null,
		data: {
			account: fields.account,
			email: fields.email,
			phone: fields.phone,
			gender: fields.gender,
			firstName: fields.firstName,
			lastName: fields.lastName,
			instagramUsername: fields.instagramUsername,
			birthday: fields.birthday,
			streetName: fields.streetName,
			houseNumber: fields.houseNumber,
			additional: fields.additional,
			postalCode: fields.postalCode,
			city: fields.city,
			country: fields.country,
			usState: fields.usState,
			caState: fields.caState,
			romanianCounty: fields.romanianCounty,
			romanianCity: fields.romanianCity,
			consents: fields.consents
		},
		consentModal,
		checkingContactDuplicity,
		romanianCountiesAndCities,
		fetchingRomanianCountiesAndCities,
		contactAccordion: { ...accordions.contactAccordion },
		aboutYouAccordion: { ...accordions.aboutYouAccordion },
		deliveryAddressAccordion: { ...accordions.deliveryAddressAccordion },
		birthdayIsRequired: accordions.aboutYouAccordion.wasValidated && (fields.birthday.day || fields.birthday.month || fields.birthday.year),
		birthdayIsComplete: fields.birthday.day && fields.birthday.month && fields.birthday.year
	};
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
	updateAccordionsAction,
	updateConsentCheckboxInput,
	updateTextInputAction,
	updateSelectInputAction,
	updateDeliveryServiceAction,
	updateBirthdayInputAction,
	goToSummaryStepAction,
	updateRomanianCityOptionsAction,
	closeConsentModalAction,
	openConsentModalAction,
	updateAddressComponentsAction,
	updateCountryAction,
	fetchRomanianCountiesAndCities: DeliveryRepository.fetchRomanianCountiesAndCities,
	checkContactDuplicityAction: RegistrationRepository.checkContactDuplicity
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CustomerStep);

/**
 * @property {?Function} props.onEditClick
 */
class Accordion extends Component {
	onEditClick = () => {
		this.props.onEditClick();
	};

	render({ title, children, isOpen, isChecked, onEditClick, isVisible, appear }) {
		if (!isVisible) {
			return null;
		}
		const editClickCallback = onEditClick instanceof Function;
		return (
			<div className="col-12 p-0">
				<div className={style.accordion}>
					<i className={`fa fa-check ${style.check + (isChecked ? ` ${style.visible}` : '')}`} />
					<span className={style.title}>{title}</span>
					{(isOpen || !isChecked || !editClickCallback) ? null :
						<span className={style.edit} onClick={this.onEditClick}>Edit<i className={style['arrow-down']} /></span>
					}
				</div>
				<SlideDownReveal visible={isOpen} appear={appear}>{children}</SlideDownReveal>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./components/blocks/raffleForm/CustomerStep.js